import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDisableScrollSnap, useModal } from "../../util/hook";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import styled from "styled-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useColumn } from "../../util/hook";
import Header from "../organisms/Header";
import Footer from "../organisms/Footer";
import ColumnView from "../organisms/ColumnView";

const extractGoogleDriveFileId = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const filePathMatch = trimmed.match(/\/file\/d\/([^/]+)/);
  if (filePathMatch?.[1]) return filePathMatch[1];

  try {
    const parsed = new URL(trimmed);
    const id = parsed.searchParams.get("id");
    return id || null;
  } catch {
    return null;
  }
};

const normalizeImageUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const driveFileId = extractGoogleDriveFileId(trimmed);
  if (driveFileId) {
    return `https://drive.google.com/thumbnail?id=${driveFileId}&sz=w1000`;
  }

  return trimmed;
};

const isValidImageUrl = (value: string) => {
  const normalizedValue = normalizeImageUrl(value);
  if (!normalizedValue) return true;

  try {
    const parsed = new URL(normalizedValue);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const ColumnEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addColumn, getColumn, updateColumn } = useColumn();
  const { showAlert } = useModal();
  useDisableScrollSnap();

  const isEditMode = !!id;

  const [isPreview, setIsPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [content, setContent] = useState("");

  // 원본 데이터 저장 (수정 모드에서 변경 여부 확인용)
  const originalData = useRef<{
    title: string;
    date: string;
    imageUrl: string;
    content: string;
  } | null>(null);

  useEffect(() => {
    const initializeEditor = async () => {
      if (isEditMode) {
        const column = getColumn(Number(id));
        if (column) {
          setTitle(column.title);
          setDate(column.date);
          setImageUrl(column.imageUrl || "");
          setContent(column.content || "");

          // 원본 데이터 저장
          originalData.current = {
            title: column.title,
            date: column.date,
            imageUrl: column.imageUrl || "",
            content: column.content || "",
          };
        } else {
          await showAlert("존재하지 않는 칼럼입니다.");
          navigate(`/column/${id}`);
        }
      } else {
        // Initial date setting for new post
        const today = new Date();
        const formattedDate = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, "0")}.${String(today.getDate()).padStart(2, "0")}`;
        setDate(formattedDate);
      }
    };

    initializeEditor();
  }, [id, isEditMode, getColumn, navigate, showAlert]);

  const modules = React.useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [
          { list: "ordered" },
          { list: "bullet" },
          { indent: "-1" },
          { indent: "+1" },
        ],
        ["link"],
        [
          { align: [] },
          {
            color: [
              "#000000",
              "#e60000",
              "#ff9900",
              "#ffff00",
              "#008a00",
              "#0066cc",
              "#9933ff",
              "#ffffff",
              "#facccc",
              "#ffebcc",
              "#ffffcc",
              "#cce8cc",
              "#cce0f5",
              "#ebd6ff",
              "#bbbbbb",
              "#f06666",
              "#ffc266",
              "#ffff66",
              "#66b966",
              "#66a3e0",
              "#c285ff",
              "#888888",
              "#a10000",
              "#b26b00",
              "#b2b200",
              "#006100",
              "#0047b2",
              "#6b24b2",
              "#444444",
              "#5c0000",
              "#663d00",
              "#666600",
              "#003700",
              "#002966",
              "#3d1466",
            ],
          },
          { background: [] },
        ],
        ["clean"],
      ],
    }),
    [],
  );

  // 날짜 형식 변환: "YYYY.MM.DD" -> "YYYY-MM-DD" (input date용)
  const dateToInputFormat = (dateStr: string): string => {
    if (!dateStr) return "";
    return dateStr.replace(/\./g, "-");
  };

  // 날짜 형식 변환: "YYYY-MM-DD" -> "YYYY.MM.DD" (표시용)
  const inputToDisplayFormat = (dateStr: string): string => {
    if (!dateStr) return "";
    return dateStr.replace(/-/g, ".");
  };

  // 변경 여부 확인 함수
  const hasChanges = (): boolean => {
    if (!originalData.current) return true;

    return (
      title !== originalData.current.title ||
      date !== originalData.current.date ||
      imageUrl !== originalData.current.imageUrl ||
      content !== originalData.current.content
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !content) {
      await showAlert("제목과 내용은 필수입니다.");
      return;
    }

    if (!isValidImageUrl(imageUrl)) {
      await showAlert("이미지 URL은 http:// 또는 https:// 형식이어야 합니다.");
      return;
    }

    // 수정 모드에서 변경 사항이 없으면 서버 통신 없이 이동
    if (isEditMode && !hasChanges()) {
      await showAlert("변경된 내용이 없습니다.");
      navigate(`/column/${id}`);
      return;
    }

    setSubmitting(true);

    try {
      const normalizedImageUrl = normalizeImageUrl(imageUrl);
      const columnData = {
        title,
        date,
        imageUrl: normalizedImageUrl,
        content,
      };

      if (isEditMode) {
        await updateColumn(Number(id), columnData);
        await showAlert("수정되었습니다.");
        navigate(`/column/${id}`, {
          replace: true,
          state: { backTo: location.state?.backTo },
        });
      } else {
        await addColumn(columnData);
        await showAlert("등록되었습니다.");
        navigate("/");
      }
    } catch (error) {
      console.error("Error saving column:", error);
      await showAlert("저장 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePreview = () => {
    setIsPreview(!isPreview);
  };

  // Preview Data
  const previewData = {
    id: 0, // Mock ID
    title: title || "제목 미리보기",
    date: date || "날짜 미리보기",
    imageUrl: normalizeImageUrl(imageUrl) || undefined,
    content: content || "<p>내용 미리보기</p>",
  };

  if (isPreview) {
    return (
      <>
        <Header />
        <Container>
          <PageTitle>미리보기</PageTitle>
          <PreviewContainer>
            <ColumnView column={previewData} />
          </PreviewContainer>
          <ButtonGroup>
            <Button onClick={togglePreview} className="cancel" type="button">
              돌아가기
            </Button>
          </ButtonGroup>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <Container>
        <PageTitle>{isEditMode ? "칼럼 수정" : "칼럼 작성"}</PageTitle>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>제목 *</Label>
            <Input
              type="text"
              placeholder="칼럼 제목을 입력하세요"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>날짜 *</Label>
            <DateInput
              type="date"
              value={dateToInputFormat(date)}
              onChange={(e) => setDate(inputToDisplayFormat(e.target.value))}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>대표 이미지 URL</Label>
            <Input
              type="url"
              placeholder="https://example.com/image.jpg"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
            />
            <HelperText>
              이미지는 선택 입력이며, 저장 시 칼럼 상단에만 노출됩니다.
            </HelperText>
          </FormGroup>

          <FormGroup>
            <Label>내용 *</Label>
            <EditorWrapper>
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={modules}
                placeholder="칼럼 내용을 입력하세요"
              />
            </EditorWrapper>
          </FormGroup>

          <ButtonGroup>
            <Button
              type="button"
              onClick={() =>
                navigate(isEditMode ? `/column/${id}` : "/", {
                  replace: isEditMode,
                  state: isEditMode
                    ? { backTo: location.state?.backTo }
                    : undefined,
                })
              }
              className="cancel"
            >
              취소
            </Button>
            <Button
              type="button"
              onClick={togglePreview}
              className="preview"
              disabled={submitting}
            >
              미리보기
            </Button>
            <Button
              type="submit"
              className="submit"
              disabled={
                !title ||
                !content ||
                submitting ||
                (isEditMode && !hasChanges())
              }
            >
              {submitting
                ? "저장 중..."
                : isEditMode
                  ? "수정 완료"
                  : "작성 완료"}
            </Button>
          </ButtonGroup>
        </Form>
      </Container>
      <Footer />
    </>
  );
};

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
  min-height: calc(100vh - 200px);

  @media (max-width: 768px) {
    padding: 30px 15px;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  color: #004094;
  margin-bottom: 30px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Form = styled.form`
  background: #fff;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 15px;
  transition: border 0.3s;

  &:focus {
    outline: none;
    border-color: #004094;
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 15px;
  transition: border 0.3s;

  &:focus {
    outline: none;
    border-color: #004094;
  }
`;

const HelperText = styled.p`
  margin-top: 8px;
  font-size: 13px;
  color: #777;
  line-height: 1.5;
`;

const EditorWrapper = styled.div`
  .ql-container {
    min-height: 400px;
    font-size: 15px;
  }

  .ql-editor {
    min-height: 400px;
  }

  margin-bottom: 50px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 15px 40px;
  font-size: 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
  transition: 0.3s;
  white-space: nowrap;

  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 15px 20px;
    text-align: center;
  }

  &.submit {
    background: #004094;
    color: #fff;
    border: none;

    &:hover {
      background: #003070;
    }
  }

  &.cancel {
    background: #f5f5f5;
    color: #666;
    border: 1px solid #ddd;

    &:hover {
      background: #e0e0e0;
    }
  }

  &.preview {
    background: #ff9800;
    color: #fff;
    border: none;

    &:hover {
      background: #f57c00;
    }
  }

  &:disabled {
    background: #ccc;
    color: #888;
    cursor: not-allowed;
    border: none;

    &:hover {
      background: #ccc;
    }
  }
`;

const PreviewContainer = styled.div`
  background: #fff;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

export default ColumnEditor;
